import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  TextField, 
  Button, 
  Tabs, 
  Tab,
  Autocomplete,
  CircularProgress,
  Alert,
  Collapse,
  FormControlLabel,
  Checkbox,
  Typography,
  IconButton,
  Switch
} from '@mui/material';
import { Close as CloseIcon, AutoAwesome as AIIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import { colors } from '../../themes/typography';
import AnimatedTypography from '../shared/AnimatedTypography';
import AIRecommendationView from './AIRecommendationView';
import { DEFAULT_EXPERIMENT_WEEKS } from '../../constants/experiments';
import { formatLocalDate } from '../../utils/dateUtils';
import { getApiErrorMessage } from '../../utils/apiError';
import AppliedPricingDialog from '../shared/AppliedPricingDialog';
import SlotPicker from '../shared/SlotPicker';

// Un tab con nombre en vez de un índice comparado en tres sitios: con cuatro tipos la
// aritmética 0/1 ya no aguanta.
const TAB_TO_TYPE = {
  discount: 'promo_precio_unitario',
  replacement: 'reemplazo_producto',
  lane: 'ampliar_carril',
  increase: 'alza_precio_sugerido'
};
// Helper function to format numbers in compact format
const formatCompactNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  const num = Math.abs(parseFloat(value));
  
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  
  return num.toString();
};

/**
 * Problem Analysis Panel Component
 * Side panel for AI analysis and experiment creation
 */
const ProblemAnalysisPanel = ({
  problemId,
  clientId,
  onClose,
  onDataRefresh,
}) => {
  // Nadie le pasaba `t`, así que el panel entero salía con las claves o en español fuera cual fuera el idioma.
  const { t } = useTranslation();
  // Form state
  const [formData, setFormData] = React.useState({
    experimentType: 'promo_precio_unitario',
    discount: 15,
    markup: 15,
    targetPrice: '',
    pricingMode: 'discount', // 'discount' = % sobre el precio, 'price' = precio final tecleado
    dateFrom: null,
    dateTo: null,
    newDProductId: '', // For product replacement / lane expansion beneficiary
    innovation: false,
    status: 'pending' // Default status
  });

  // Tab state
  const [activeTab, setActiveTab] = React.useState('discount');
  const [submitError, setSubmitError] = React.useState(null);
  const [priceReview, setPriceReview] = React.useState(null);

  // Creation mode state ('manual' or 'ai') - defaults to 'ai' (Recommendation AI)
  const [creationMode, setCreationMode] = React.useState('ai');

  // AI loading state for 2-second animation - starts true when defaulting to AI mode
  const [aiLoading, setAiLoading] = React.useState(true);

  // Problem data loading state
  const [problemData, setProblemData] = React.useState({
    loading: true,
    data: null,
    error: null
  });

  // All products for replacement (separate from problem data)
  const [allProducts, setAllProducts] = React.useState([]);

  // Lane picker state for the expansion tab — the donor is the problem's product, so only the
  // machine and the chosen lanes are variable here.
  const [selectedSlots, setSelectedSlots] = React.useState([]);
  const [machineSlots, setMachineSlots] = React.useState([]);
  const [slotsLoading, setSlotsLoading] = React.useState(false);

  // Form submission state
  const [submitting, setSubmitting] = React.useState(false);

  // Warning state for validation
  const [warnings, setWarnings] = React.useState([]);

  // Fetch machine slots once the problem's machine is known (same endpoint the kanban form uses)
  const panelDStoreId = problemData.data?.store?.id;
  React.useEffect(() => {
    if (!panelDStoreId || !clientId) {
      setMachineSlots([]);
      setSelectedSlots([]);
      return;
    }
    const fetchSlots = async () => {
      setSlotsLoading(true);
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(
          `${API_BASE_URL}/experiments/machine-slots`,
          { params: { clientId, dStoreId: panelDStoreId }, headers: createAuthHeader(token) }
        );
        setMachineSlots(response.data.slots || []);
      } catch (err) {
        setMachineSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    };
    fetchSlots();
  }, [panelDStoreId, clientId]);

  // Handle experiment type tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Update experiment type based on tab
    setFormData(prev => ({ ...prev, experimentType: TAB_TO_TYPE[newValue] }));
  };

  // Handle creation mode tab change
  const handleCreationModeChange = (event, newValue) => {
    const mode = newValue === 0 ? 'manual' : 'ai';
    
    if (mode === 'ai' && creationMode !== 'ai') {
      // Show AI loading animation for 2 seconds
      setAiLoading(true);
      setTimeout(() => {
        setAiLoading(false);
      }, 2000);
    }
    
    setCreationMode(mode);
  };

  // Initialize default dates
  React.useEffect(() => {
    if (problemId) {
      // Default to future dates for new experiments
      const defaultStartDate = new Date();
      defaultStartDate.setDate(defaultStartDate.getDate() + 1); // Tomorrow

      const defaultEndDate = new Date(defaultStartDate);
      // 8 weeks by default
      defaultEndDate.setDate(defaultEndDate.getDate() + (DEFAULT_EXPERIMENT_WEEKS * 7));

      setFormData(prev => ({
        ...prev,
        dateFrom: defaultStartDate,
        dateTo: defaultEndDate
      }));
    }
  }, [problemId]);

  // Clear AI loading animation after 2 seconds on initial mount (when defaulting to AI mode)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setAiLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Load problem data and products when component mounts
  React.useEffect(() => {
    if (problemId && clientId) {
      loadProblemData();
      loadAllProducts();
    }
  }, [problemId, clientId]);

  const loadProblemData = async () => {
    try {
      setProblemData(prev => ({ ...prev, loading: true, error: null }));
      
      const token = localStorage.getItem('access_token');
      const headers = createAuthHeader(token);

      const response = await axios.get(
        `${API_BASE_URL}/problems/${problemId}/details?clientId=${clientId}`,
        { headers }
      );

      setProblemData({
        loading: false,
        data: response.data,
        error: null
      });
    } catch (error) {
      console.error('Error loading problem data:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load problem data';
      setProblemData({
        loading: false,
        data: null,
        error: errorMessage
      });
    }
  };

  const loadAllProducts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = createAuthHeader(token);

      const response = await axios.get(
        `${API_BASE_URL}/all-products?clientId=${clientId}`,
        { headers }
      );

      setAllProducts(response.data.products || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  // Date validation function - EXACT same as popover
  const validateExperimentDates = React.useCallback((dateFrom, dateTo) => {
    if (!dateFrom || !dateTo) {
      return { warnings: [] };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day for accurate comparison
    
    const experimentStart = new Date(dateFrom);
    experimentStart.setHours(0, 0, 0, 0);
    
    const experimentEnd = new Date(dateTo);
    experimentEnd.setHours(23, 59, 59, 999); // End of day for accurate comparison
    
    const warnings = [];
    
    // Check for past dates
    if (experimentEnd < today) {
      warnings.push({
        type: 'date',
        severity: 'warning',
        message: t('experimentInPast') || 'Experiment dates are in the past. This will create a finished experiment.'
      });
    } else if (experimentStart <= today && experimentEnd >= today) {
      warnings.push({
        type: 'date',
        severity: 'info',
        message: t('experimentRunning') || 'Experiment will start immediately and be marked as running.'
      });
    }
    
    return { warnings };
  }, [t]);

  // Validate dates whenever they change
  React.useEffect(() => {
    const { warnings: newWarnings } = validateExperimentDates(formData.dateFrom, formData.dateTo);
    setWarnings(newWarnings);
  }, [formData.dateFrom, formData.dateTo, validateExperimentDates]);

  // Handle form submission - EXACT same logic as popover
  const handleSubmit = async (pricingOverride = null) => {
    // Basic validation
    if (!formData.dateFrom || !formData.dateTo) {
      // Show error message
      console.error('Please select start and end dates');
      return;
    }

    // Type-specific validation
    if (formData.experimentType === 'promo_precio_unitario') {
      const discount = formData.discount;
      if (discount === '' || discount === null || discount === undefined || discount < 1 || discount > 90) {
        console.error('Please enter a discount between 1% and 90%');
        return;
      }
    }

    if (formData.experimentType === 'alza_precio_sugerido') {
      if (formData.pricingMode === 'price') {
        // "Above the current price" can only be checked server-side — the form does not know it.
        const parsedPrice = parseFloat(formData.targetPrice);
        if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
          setSubmitError(t('pleaseEnterFinalPrice') || 'Please enter a valid final price');
          return;
        }
      } else if (!formData.markup || formData.markup < 1) {
        // Sin tope superior: una subida puede superar el +100%.
        setSubmitError(t('pleaseEnterMarkup') || 'Please enter a price increase of at least 1%');
        return;
      }
    }

    if ((formData.experimentType === 'reemplazo_producto' || formData.experimentType === 'ampliar_carril')
        && !formData.newDProductId) {
      setSubmitError(t('pleaseSelectReplacementProduct') || 'Please select a replacement product');
      return;
    }

    // D6: la ampliación se define por QUÉ carriles cambian de manos — sin al menos uno no hay
    // experimento.
    if (formData.experimentType === 'ampliar_carril' && selectedSlots.length < 1) {
      setSubmitError(t('ampliarCarrilRequiresLane') || 'Selecciona al menos un carril para la ampliación');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    
    try {
      const token = localStorage.getItem('access_token');
      
      // Build request body for problem-specific endpoint
      const requestBody = {
        experimentType: formData.experimentType,
        dateFrom: formatLocalDate(formData.dateFrom),
        dateTo: formatLocalDate(formData.dateTo),
        status: formData.status, // Use selected status from form
        isAI: true // Flag as AI-assisted experiment from Acciones Comerciales
      };

      // Add type-specific fields
      if (formData.experimentType === 'promo_precio_unitario') {
        requestBody.discount = formData.discount;
      } else if (formData.experimentType === 'alza_precio_sugerido') {
        // Never send `discount` for a rise: exactly ONE magnitude, % or final price.
        if (formData.pricingMode === 'price') {
          requestBody.targetPrice = parseFloat(formData.targetPrice);
        } else {
          requestBody.markup = formData.markup;
        }
      } else if (formData.experimentType === 'reemplazo_producto' || formData.experimentType === 'ampliar_carril') {
        requestBody.newDProductId = parseInt(formData.newDProductId);
      }

      // La ampliación nombra sus carriles (D6); el resto sigue con la regla D8 del backend
      // (sin `selectionIds` → todos los del producto).
      if (formData.experimentType === 'ampliar_carril') {
        requestBody.selectionIds = selectedSlots;
      }

      // Add innovation flag for replacement-shaped types
      if (formData.experimentType === 'reemplazo_producto' || formData.experimentType === 'ampliar_carril') {
        requestBody.innovation = formData.innovation;
      }
      if (formData.status === 'accepted' && !pricingOverride) {
        const preview = await axios.post(
          `${API_BASE_URL}/experiments/pricing-preview?clientId=${clientId}`,
          { ...requestBody, dStoreId: problem.dStoreId || store?.id, dProductId: problem.dProductId || product?.id },
          { headers: createAuthHeader(token) }
        );
        if (preview.data?.pricing) { setPriceReview(preview.data.pricing); return; }
      }
      if (pricingOverride) requestBody.pricingOverride = pricingOverride;

      const response = await axios.post(
        `${API_BASE_URL}/problems/${problemId}/experiments/manual?clientId=${clientId}`,
        requestBody,
        { headers: createAuthHeader(token) }
      );
      // Success. No confirmation of what the ERP applied: the operator already saw and
      // approved those exact tenders in the preview, so repeating them after the fact only
      // adds a click.
      if (onDataRefresh) {
        onDataRefresh();
      }
      onClose();
    } catch (error) {
      console.error('Error creating experiment:', error);
      setSubmitError(getApiErrorMessage(error, 'Failed to create experiment'));
    } finally {
      setSubmitting(false);
    }
  };

  // Get metric icon component
  const getMetricIcon = (icon, status) => {
    const iconColor = status === 'danger' ? '#f44336' : status === 'warning' ? '#ff9800' : '#4caf50';
    
    switch (icon) {
      case 'arrow_down':
        return '↓';
      case 'triangle_up':
        return '△';
      default:
        return '●';
    }
  };

  // Render problem context using the API context data
  const renderProblemContext = (problem) => {
    // First try to use the new context data from API
    if (problem && problem.context && problem.context.metrics) {
      const { context } = problem;
      
      return (
        <Box>
          {context.metrics.map((metric, index) => {
            const icon = getMetricIcon(metric.icon, metric.status);
            const iconColor = metric.status === 'danger' ? '#f44336' : 
                             metric.status === 'warning' ? '#ff9800' : '#4caf50';
            
            // Build main text content
            const mainText = `${formatCompactNumber(metric.value)} unidades${
              metric.detail?.category ? ` en ${metric.detail.category}` : ''
            }${metric.detail?.sector ? ` (${metric.detail.sector})` : ''}`;
            
            // Build description text
            const descText = metric.type === 'current' ? 'Rendimiento del producto (6 semanas)' :
                           metric.type === 'benchmark' ? `Promedio de categoría en ${metric.detail?.sector || 'sector'}` :
                           metric.type === 'market' ? 'Total de la ubicación' : '';
            
            return (
              <Box key={index} sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography sx={{ 
                  fontSize: '14px', 
                  color: iconColor,
                  minWidth: '16px',
                  textAlign: 'center'
                }}>
                  {icon}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <AnimatedTypography
                    typewriterSpeed={26} // 50% faster (51 * 0.5)
                    startDelay={index * 96} // 50% faster delays (192 * 0.5)
                    wordMode={false} // Character-by-character for better fade effect
                    showCursor={false}
                    sx={{ 
                      fontSize: '12px', 
                      fontWeight: 500,
                      color: colors.text.primary,
                      lineHeight: 1.3
                    }}
                  >
                    {mainText}
                  </AnimatedTypography>
                  <AnimatedTypography
                    typewriterSpeed={23} // 50% faster (45 * 0.5)
                    startDelay={index * 96 + 256} // 50% faster (512 * 0.5)
                    wordMode={false} // Character-by-character for better fade effect
                    showCursor={false}
                    sx={{ 
                      fontSize: '11px',
                      color: colors.text.secondary,
                      lineHeight: 1.2
                    }}
                  >
                    {descText}
                  </AnimatedTypography>
                </Box>
              </Box>
            );
          })}
          
          {/* Show insights if available - same styling as metrics */}
          {context.insights && context.insights.length > 0 && (
            context.insights.slice(0, 2).map((insight, index) => {
              const baseDelay = context.metrics.length * 150 + 1000; // Start after all metrics
              return (
                <Box key={`insight-${index}`} sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Typography sx={{ 
                    fontSize: '14px', 
                    color: '#4caf50',
                    minWidth: '16px',
                    textAlign: 'center'
                  }}>
                    💡
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <AnimatedTypography
                      typewriterSpeed={29} // 50% faster (58 * 0.5)
                      startDelay={baseDelay + (index * 192)} // 50% faster (384 * 0.5)
                      wordMode={false} // Character-by-character for better fade effect
                      showCursor={index === context.insights.slice(0, 2).length - 1} // Show cursor on last insight
                      sx={{ 
                        fontSize: '12px', 
                        fontWeight: 500,
                        color: colors.text.primary,
                        lineHeight: 1.3
                      }}
                    >
                      {insight.text}
                    </AnimatedTypography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      );
    }

    // Fallback to old logic if context is not available
    if (!problem || !problem.problemInfo) {
      return (
        <Typography sx={{ fontSize: '12px', color: colors.text.secondary }}>
          No context information available
        </Typography>
      );
    }

    const { problemInfo, analysisType } = problem;
    
    if (analysisType === 'seasonal_underperformance') {
      const items = [];
      let itemIndex = 0;
      
      if (problemInfo.market_median) {
        items.push({
          text: `📊 Market analysis: ${formatCompactNumber(problemInfo.market_median)} units (6 weeks)`,
          delay: itemIndex * 200
        });
        itemIndex++;
      }
      
      if (problemInfo.category_median) {
        items.push({
          text: `📦 Category benchmark: ${formatCompactNumber(problemInfo.category_median)} units in ${problemInfo.product_category || 'category'} (6 weeks)`,
          delay: itemIndex * 200
        });
        itemIndex++;
      }
      
      if (problemInfo.store_cluster_weekly_avg) {
        items.push({
          text: `🏪 Store cluster avg: ${formatCompactNumber(problemInfo.store_cluster_weekly_avg)} units/week`,
          delay: itemIndex * 200
        });
        itemIndex++;
      }
      
      if (problemInfo.product_weekly_avg) {
        items.push({
          text: `📉 Product performance: ${formatCompactNumber(problemInfo.product_weekly_avg)} units/week${problemInfo.weeks_analyzed ? ` (${problemInfo.weeks_analyzed} weeks analyzed)` : ''}`,
          delay: itemIndex * 200
        });
        itemIndex++;
      }
      
      return (
        <Box>
          {items.map((item, index) => (
            <AnimatedTypography
              key={index}
              typewriterSpeed={24} // 50% faster (48 * 0.5)
              startDelay={item.delay * 0.32} // 50% faster delays (0.64 * 0.5)
              wordMode={false} // Character-by-character for better fade effect
              showCursor={index === items.length - 1} // Show cursor on last item
              sx={{ fontSize: '12px', mb: 1, color: colors.text.primary }}
            >
              {item.text}
            </AnimatedTypography>
          ))}
        </Box>
      );
    }
    
    // Fallback for other analysis types
    return (
      <AnimatedTypography
        typewriterSpeed={26} // 50% faster (51 * 0.5)
        startDelay={0}
        wordMode={false} // Character-by-character for better fade effect
        showCursor={true}
        sx={{ fontSize: '12px', color: colors.text.primary }}
      >
        Problem detected requiring attention. Analysis type: {analysisType}
      </AnimatedTypography>
    );
  };

  if (problemData.loading) {
    return (
      <Box sx={{
        p: 3,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px'
      }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (problemData.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ fontSize: '13px' }}>
          Error loading problem data: {problemData.error}
        </Alert>
      </Box>
    );
  }

  const { problem, product, store, existingAction } = problemData.data;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ 
        height: '100%', 
        overflow: 'auto',
        backgroundColor: '#fff'
      }}>
        <AppliedPricingDialog open={Boolean(priceReview)} pricing={priceReview} onClose={() => setPriceReview(null)} onConfirm={(pricingOverride) => { setPriceReview(null); handleSubmit(pricingOverride); }} />
        {submitError && (
          <Alert
            severity="error"
            sx={{ mx: 2, mt: 2, fontSize: '13px' }}
            // El banner vive arriba del drawer y el formulario abajo: sin este scroll el error
            // de validación quedaba fuera de pantalla y parecía que el botón no hacía nada.
            ref={(node) => { if (node) node.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }}
          >
            {submitError}
          </Alert>
        )}
        
        {/* Title Row - Outside grey background */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2,
          mt: 2,  // Add top margin (16px)
          px: 2   // Add horizontal padding (16px)
        }}>
          <Typography sx={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#37352f',
            lineHeight: 1.3
          }}>
            {t('problemAnalysisTitle')}
          </Typography>
          
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              p: 0.5,
              color: '#787774',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                color: '#37352f'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Header Section - More whitish background */}
        <Box sx={{ 
          borderBottom: '1px solid #E0E0E0',
          mb: 2,
          backgroundColor: '#FCFCFC',
          border: '1px solid #e7e5e4',
          mx: -2  // Negative margin to extend edge-to-edge
        }}>
          {/* Centered content container */}
          <Box sx={{ 
            py: 2,
            px: 2,
            mx: 2,  // 16px horizontal margin
            my: 1   // 8px vertical margin (reduced)
          }}>
            {/* Product Section */}
            <Box sx={{ mb: '4px' }}>
              {/* Product Name */}
              <Typography sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#37352f',
                lineHeight: 1.2
              }}>
                {product.name}
              </Typography>
            </Box>

            {/* Location Section - Two Lines */}
            <Box>
              <Typography sx={{
                fontSize: '10px',
                fontWeight: 400,
                color: '#787774',
                lineHeight: 1.4
              }}>
                {store.storeCluster}
              </Typography>
              <Typography sx={{
                fontSize: '10px',
                fontWeight: 400,
                color: '#787774',
                lineHeight: 1.4,
                mt: '2px'
              }}>
                {t('machine')} {store.storeId}
              </Typography>
            </Box>

            {/* Metrics Pills */}
            <Box sx={{ 
              display: 'flex', 
              gap: '8px', 
              mt: '12px'
            }}>
              {/* Economic Impact Pill */}
              <Box sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '6px',
                padding: '6px 10px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
                flex: 1,
                border: '1px solid #e7e5e4'
              }}>
                <Typography sx={{
                  fontSize: '11px',
                  color: '#787774'
                }}>
                  {t('opportunityCost')}
                </Typography>
                <Typography sx={{
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: '#37352f'
                }}>
                  €{problem.estimatedLoss?.toFixed(2) || '0.00'}
                </Typography>
              </Box>

              {/* Category Pill */}
              <Box sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '6px',
                padding: '6px 10px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '2px',
                flex: 1,
                border: '1px solid #e7e5e4'
              }}>
                <Typography sx={{
                  fontSize: '11px',
                  color: '#787774'
                }}>
                  {t('category')}
                </Typography>
                <Typography sx={{
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: '#37352f'
                }}>
                  {product.category}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Content Section */}
        <Box sx={{ 
          pb: 2,
          px: 2  // Add horizontal padding (16px)
        }}>
          {/* Context Section - Flat design. Desactivada (Jorge 2026-08-05): las métricas/insight
              del diagnóstico no aportaban en este panel — quitar el `false &&` para reactivarla. */}
          {false && (
          <Box sx={{ mb: 3 }}>
            {/* AI Context Header with Icon */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2
            }}>
              <IconButton
                size="small"
                sx={{
                  padding: '4px',
                  backgroundColor: colors.accent.purple + '10',
                  '&:hover': {
                    backgroundColor: colors.accent.purple + '20'
                  }
                }}
              >
                <AIIcon sx={{ 
                  fontSize: '16px',
                  color: colors.accent.purple
                }} />
              </IconButton>
              <Typography sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#37352f'
              }}>
                {t('context')} - {t('intelligentDiagnosis')}
              </Typography>
            </Box>
            
            {/* Context content */}
            <Box sx={{ pl: 0, mb: 2 }}>
              {renderProblemContext(problem)}
            </Box>
            
            {/* Horizontal separator */}
            <Box sx={{
              borderBottom: '1px solid #E0E0E0',
              mb: 3,
              mx: -2  // Negative margins to extend edge-to-edge
            }} />
          </Box>
          )}

        {/* Experiment Creation Form */}
        <Box sx={{ mb: 2 }}>
          {/* Creation Mode Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            mb: 2 
          }}>
            {/* Manual Creation Button */}
            <Button
              fullWidth
              variant={creationMode === 'manual' ? 'contained' : 'outlined'}
              onClick={(e) => handleCreationModeChange(e, 0)}
              sx={{
                backgroundColor: creationMode === 'manual' ? '#f0f0f0' : 'transparent',
                color: creationMode === 'manual' ? '#37352f' : '#787774',
                borderColor: creationMode === 'manual' ? '#000' : '#e0e0e0',
                borderWidth: creationMode === 'manual' ? '1.5px' : '1px',
                borderStyle: 'solid',
                fontSize: '12px',
                textTransform: 'none',
                fontWeight: creationMode === 'manual' ? 600 : 500,
                py: 1.5,
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: creationMode === 'manual' ? '#e8e8e8' : '#f8f8f8',
                  borderColor: creationMode === 'manual' ? '#000' : '#d0d0d0',
                }
              }}
            >
              {t('commercialActions') || 'Acciones Comerciales'}
            </Button>

            {/* AI Recommendation Button */}
            <Button
              fullWidth
              variant="contained"
              onClick={(e) => handleCreationModeChange(e, 1)}
              startIcon={
                <AIIcon sx={{ 
                  fontSize: '16px',
                  color: creationMode === 'ai' ? 'white' : '#6B46C1'
                }} />
              }
              sx={{
                backgroundColor: creationMode === 'ai' ? '#6B46C1' : '#f8f7ff',
                color: creationMode === 'ai' ? 'white' : '#6B46C1',
                fontSize: '12px',
                textTransform: 'none',
                fontWeight: 500,
                py: 1.5,
                borderRadius: '8px',
                boxShadow: creationMode === 'ai' ? '0 2px 8px rgba(107, 70, 193, 0.3)' : 'none',
                '&:hover': {
                  backgroundColor: creationMode === 'ai' ? '#5a3ba3' : '#efeeff',
                }
              }}
            >
              {t('aiRecommendation') || 'Recomendación IA'}
            </Button>
          </Box>

          {/* Main Content - AI Recommendation or Manual Form */}
          <Box sx={{ minHeight: '250px', position: 'relative' }}>
            {creationMode === 'ai' ? (
              aiLoading ? (
                <Box sx={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: 2
                }}>
                  <Box
                    sx={{
                      animation: 'spin 1s linear infinite',
                      '@keyframes spin': {
                        '0%': {
                          transform: 'rotate(0deg)',
                        },
                        '100%': {
                          transform: 'rotate(360deg)',
                        },
                      },
                    }}
                  >
                    <AIIcon sx={{ 
                      fontSize: '32px',
                      color: '#6B46C1'
                    }} />
                  </Box>
                  <Typography sx={{
                    fontSize: '13px',
                    color: '#787774',
                    textAlign: 'center'
                  }}>
                    {t('generatingAIRecommendation') || 'Generando recomendación IA...'}
                  </Typography>
                </Box>
              ) : (
                <AIRecommendationView
                  problem={problem}
                  storeId={store.storeId}
                  productId={product.id}
                  clientId={clientId}
                  token={localStorage.getItem('access_token')}
                  onClose={onClose}
                  onDataRefresh={onDataRefresh}
                  t={t}
                />
              )
            ) : (
            <>
              {/* Experiment Type Tabs */}
              <Box sx={{ mb: 2 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                minHeight: 'auto',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '12px',
                  minHeight: '32px',
                  py: 1,
                  color: '#787774',
                  '&.Mui-selected': {
                    color: '#37352f'
                  }
                },
                '& .MuiTabs-indicator': {
                  height: '2px',
                  backgroundColor: '#37352f'
                }
              }}
            >
              <Tab
                value="discount"
                label={t('priceDiscount') || 'Descuento de Precio'}
              />
              <Tab
                value="replacement"
                label={t('productReplacement') || 'Reemplazo Producto'}
              />
              <Tab
                value="lane"
                label={t('laneExpansion') || 'Ampliar Carril'}
              />
              <Tab
                value="increase"
                label={t('priceIncrease') || 'Alza de Precio'}
              />
            </Tabs>
          </Box>

          {/* Este problema ya tiene una acción y el endpoint la reescribe en sitio (dedup por
              problema). Se avisa antes de guardar para que el operador no la pierda en silencio. */}
          {existingAction && existingAction.experimentType !== TAB_TO_TYPE[activeTab] && (
            <Alert severity="warning" sx={{ mb: 1.5, fontSize: '11px', '& .MuiAlert-message': { fontSize: '11px', lineHeight: 1.3 } }}>
              {t('problemActionWillBeReplaced', {
                type: t(`${existingAction.experimentType}_label`, { defaultValue: existingAction.experimentType }),
                defaultValue: `Este problema ya tiene una acción (${existingAction.experimentType}). Al guardar, se reemplazará por la nueva.`
              })}
            </Alert>
          )}

          {/* Warnings */}
          <Collapse in={warnings.length > 0}>
            <Alert
              severity={warnings.find(w => w.type === 'date' && w.severity === 'warning') ? 'warning' : 'info'}
              sx={{
                mb: 1.5,
                fontSize: '11px',
                '& .MuiAlert-message': {
                  fontSize: '11px',
                  lineHeight: 1.3
                },
                '& .MuiAlert-icon': {
                  fontSize: '16px'
                }
              }}
            >
              {warnings.map((warning, index) => (
                <span key={index}>
                  {warning.message}
                  {index < warnings.length - 1 && ' '}
                </span>
              ))}
            </Alert>
          </Collapse>

          {/* Form Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            
            {/* Tab-specific Fields */}
            {activeTab === 'discount' && (
              <TextField
                label={t('discount') || 'Discount (%)'}
                type="number"
                size="small"
                value={formData.discount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setFormData(prev => ({ ...prev, discount: '' }));
                  } else {
                    const numValue = parseInt(value);
                    if (!isNaN(numValue)) {
                      setFormData(prev => ({ ...prev, discount: numValue }));
                    }
                  }
                }}
                inputProps={{ min: 1, max: 90 }}
                fullWidth
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '13px'
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '13px',
                    py: 1.5
                  }
                }}
              />
            )}

            {activeTab === 'increase' && (
              // Espejo del descuento: exactamente UNA magnitud, el % o el precio final.
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                {formData.pricingMode === 'price' ? (
                  <TextField
                    label={t('finalPrice') || 'Precio final'}
                    size="small"
                    value={formData.targetPrice}
                    onChange={(e) => setFormData(prev => ({ ...prev, targetPrice: e.target.value }))}
                    inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
                    fullWidth
                    sx={{
                      '& .MuiInputLabel-root': { fontSize: '13px' },
                      '& .MuiInputBase-input': { fontSize: '13px', py: 1.5 }
                    }}
                  />
                ) : (
                  <TextField
                    label={t('markupLabel') || 'Subida (%)'}
                    type="number"
                    size="small"
                    value={formData.markup}
                    onChange={(e) => setFormData(prev => ({ ...prev, markup: parseInt(e.target.value, 10) || 0 }))}
                    inputProps={{ min: 1 }}
                    fullWidth
                    sx={{
                      '& .MuiInputLabel-root': { fontSize: '13px' },
                      '& .MuiInputBase-input': { fontSize: '13px', py: 1.5 }
                    }}
                  />
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, minWidth: 80 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: colors.text.secondary }}>
                    {t('finalPrice') || 'Precio final'}
                  </Typography>
                  <Switch
                    checked={formData.pricingMode === 'price'}
                    onChange={(event) => {
                      setFormData(prev => ({
                        ...prev,
                        pricingMode: event.target.checked ? 'price' : 'discount'
                      }));
                    }}
                    size="small"
                  />
                </Box>
              </Box>
            )}

            {(activeTab === 'replacement' || activeTab === 'lane') && (
              <>
                <Autocomplete
                  options={allProducts}
                  getOptionLabel={(option) => option.name || ''}
                  value={allProducts.find(p => p.id === formData.newDProductId) || null}
                  onChange={(_, newValue) => {
                    setFormData(prev => ({ ...prev, newDProductId: newValue?.id || '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={activeTab === 'lane'
                        ? (t('beneficiaryProductLabel') || 'Producto que gana el carril')
                        : (t('replacementProduct') || 'Replacement Product')}
                      size="small"
                      sx={{
                        '& .MuiInputLabel-root': {
                          fontSize: '13px'
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '13px',
                          py: 1.5
                        }
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-option': {
                      fontSize: '13px'
                    }
                  }}
                />

                {/* Lane picker — which lanes the donor (the problem's product) cedes. One or
                    more; without it Discovery would create lane-less rows (D5). */}
                {activeTab === 'lane' && (
                  machineSlots.some(s => s.dProductId === parseInt(product.id)) ? (
                    <SlotPicker
                      slots={machineSlots}
                      productId={parseInt(product.id)}
                      selectedSlots={selectedSlots}
                      onChange={setSelectedSlots}
                      loading={slotsLoading}
                      t={t}
                    />
                  ) : !slotsLoading && (
                    // D6/D8: sin carril en el planograma no hay ampliación posible — se explica
                    // aquí en vez de dejar que el backend devuelva un 400.
                    <Alert severity="warning" sx={{ fontSize: '12px' }}>
                      {t('productNotInPlanogram') || 'El producto no está en el planograma actual de la máquina'}
                    </Alert>
                  )
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.innovation}
                      onChange={(e) => setFormData(prev => ({ ...prev, innovation: e.target.checked }))}
                      size="small"
                    />
                  }
                  label={t('innovation') || 'New Product in Machine'}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '13px',
                      color: '#37352f'
                    }
                  }}
                />
              </>
            )}

            {/* Date Pickers */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <DatePicker
                label={t('startDate') || 'Start Date'}
                value={formData.dateFrom}
                onChange={(date) => setFormData(prev => ({ ...prev, dateFrom: date }))}
                slots={{ textField: TextField }}
                slotProps={{ 
                  textField: { 
                    size: 'small', 
                    sx: { 
                      flex: 1,
                      '& .MuiInputLabel-root': {
                        fontSize: '13px'
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '13px',
                        py: 1.5
                      }
                    } 
                  } 
                }}
                enableAccessibleFieldDOMStructure={false}
              />
              <DatePicker
                label={t('endDate') || 'End Date'}
                value={formData.dateTo}
                onChange={(date) => setFormData(prev => ({ ...prev, dateTo: date }))}
                slots={{ textField: TextField }}
                slotProps={{ 
                  textField: { 
                    size: 'small', 
                    sx: { 
                      flex: 1,
                      '& .MuiInputLabel-root': {
                        fontSize: '13px'
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '13px',
                        py: 1.5
                      }
                    } 
                  } 
                }}
                enableAccessibleFieldDOMStructure={false}
              />
            </Box>

            {/* Status Selection */}
            <Autocomplete
              options={[
                { value: 'pending', label: t('pending') || 'Pendiente' },
                { value: 'accepted', label: t('accepted') || 'Aceptado' }
              ]}
              value={formData.status ? { value: formData.status, label: formData.status === 'pending' ? (t('pending') || 'Pendiente') : (t('accepted') || 'Aceptado') } : null}
              onChange={(_, newValue) => {
                setFormData(prev => ({ ...prev, status: newValue?.value || 'pending' }));
              }}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t('status') || 'Estado'} 
                  size="small"
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '13px'
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '13px',
                      py: 1.5
                    }
                  }}
                />
              )}
              sx={{
                '& .MuiAutocomplete-option': {
                  fontSize: '13px'
                }
              }}
            />
          </Box>

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1.5,
            mt: 1.5,
            pt: 1.5,
            borderTop: `1px solid #e0e0e0`
          }}>
            <Button
              fullWidth
              variant="contained"
              // Nunca pasar handleSubmit directo al onClick: el MouseEvent entraría como
              // `pricingOverride`, acabaría en el body y axios revienta con "Converting
              // circular structure to JSON" sin llegar a mandar nada.
              onClick={() => handleSubmit()}
              disabled={submitting}
              sx={{
                backgroundColor: '#1976d2',
                color: 'white',
                fontSize: '13px',
                textTransform: 'none',
                fontWeight: 500,
                py: 1,
                '&:hover': {
                  backgroundColor: '#115293'
                }
              }}
            >
              {submitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t('createExperiment') || 'Create Experiment'
              )}
            </Button>
            
            <Button
              onClick={onClose}
              sx={{
                color: '#666',
                fontSize: '13px',
                textTransform: 'none',
                py: 1
              }}
            >
              {t('cancel') || 'Cancel'}
            </Button>
          </Box>
            </>
            )}
          </Box>
        </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default ProblemAnalysisPanel;
